import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface EmailHealthStats {
  subscribed: number;
  unsubscribed: number;
  bounced: number;
  total: number;
  unsubscribe_rate: number;
}

export interface WebhookStats {
  email_health: EmailHealthStats;
  webhook_config: {
    secret_configured: boolean;
    endpoint: string;
  };
  last_updated: string;
}

class EmailHealthService {
  private getAuthHeaders() {
    return authService.getAuthHeaders();
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get email health statistics
   */
  async getEmailHealthStats(): Promise<EmailHealthStats> {
    const response = await fetch(`${API_BASE_URL}/api/leads/email-health`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Get webhook configuration and stats
   */
  async getWebhookStats(): Promise<WebhookStats> {
    const response = await fetch(`${API_BASE_URL}/api/webhooks/stats`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Get email status for a specific lead
   */
  async getLeadEmailStatus(leadId: number): Promise<{
    email_status: string;
    unsubscribed_at?: string;
    unsubscribe_reason?: string;
    mailerlite_subscriber_id?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}/email-status`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Resubscribe a lead (manual override)
   */
  async resubscribeLead(leadId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}/resubscribe`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Test webhook functionality (for development)
   */
  async testWebhook(): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/webhooks/test`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Format email health stats for display
   */
  formatHealthStats(stats: EmailHealthStats) {
    return {
      ...stats,
      unsubscribe_rate_display: `${stats.unsubscribe_rate.toFixed(1)}%`,
      health_status: this.getHealthStatus(stats.unsubscribe_rate)
    };
  }

  /**
   * Get health status color based on unsubscribe rate
   */
  private getHealthStatus(unsubscribeRate: number): {
    status: string;
    color: string;
    description: string;
  } {
    if (unsubscribeRate < 2) {
      return {
        status: 'Excellent',
        color: 'green',
        description: 'Very low unsubscribe rate'
      };
    } else if (unsubscribeRate < 5) {
      return {
        status: 'Good',
        color: 'blue',
        description: 'Normal unsubscribe rate'
      };
    } else if (unsubscribeRate < 10) {
      return {
        status: 'Fair',
        color: 'yellow',
        description: 'Moderate unsubscribe rate'
      };
    } else {
      return {
        status: 'Poor',
        color: 'red',
        description: 'High unsubscribe rate - needs attention'
      };
    }
  }
}

const emailHealthService = new EmailHealthService();
export default emailHealthService;